import { useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import usePostalCode, { getPostalCode } from "../../../hook/usePostalCode";
import useValueList, { getValueLabel } from "../../../hook/useValueList";
import { IAddress } from "../../../types/address";
import { Pageable } from "../../../types/common";
import { Action } from "../../../types/reducer-action";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import PaginationBar from "../../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import TableRow from "../../common/table-row";
import { useAddressDispatch, useAddressState } from "./address-manager-context";
import './style.scss';
import useAddressFormatter from "./useAddressFormatter";

interface AddressInfo extends IAddress {
  formattedAddress: string;
  municipalityName: string;
  provinceName: string;
  countryName: string;
  postalCode: string;
}

const TABLE_HEADERS: TableHeaderInfo<AddressInfo>[] = [
  { header: 'Building', accessor: 'buildingName', sortable: true },
  { header: 'Address', accessor: 'formattedAddress', sortable: true },
  { header: 'Municipality/City', accessor: 'municipalityName', presorted: 'asc', cssClass: "municipality-city-column" },
  { header: 'Province', accessor: 'provinceName', sortable: true, cssClass: "column-9rem" },
  { header: 'Country', accessor: 'countryName', sortable: true, cssClass: "column-9rem" },
  { header: 'Postal Code', accessor: 'postalCode', sortable: true, cssClass: "column-9rem" }
];

const AddressSearchResultsTable = (props: Pageable) => {
  const { pageSize } = props;

  const { dataSet, selectedDataId } = useAddressState();
  const dispatch = useAddressDispatch();

  const [cities] = useValueList('CITY');
  const [provinces] = useValueList('PROVINCE');
  const [countries] = useValueList('COUNTRY');

  const [postalCodes] = usePostalCode();

  const [formatAddress] = useAddressFormatter();

  const formattedData = useMemo(() => (
    dataSet?.map(item => ({
      ...item,
      formattedAddress: formatAddress(item),
      municipalityName: getValueLabel(cities, item.municipalityId),
      provinceName: getValueLabel(provinces, item.provinceId),
      countryName: getValueLabel(countries, item.countryId),
      postalCode: getPostalCode(postalCodes, item.postalCodeId)
    }) as AddressInfo)
      .sort((a, b) => a.municipalityName.localeCompare(b.municipalityName)
        || a.formattedAddress.localeCompare(b.formattedAddress))
  ), [dataSet]);

  const [sortedData, setSortedData] = useState<AddressInfo[]>([]);
  const [pagedData, setPagedData] = useState<IAddress[]>([]);

  const selectItem = (item?: IAddress) => dispatch(new Action(DataEntryEvent.SelectData, item?.id));

  useEffect(() => {
    selectItem(pagedData.find(item => item.id === selectedDataId))
  }, [pagedData, selectedDataId]);

  return (
    <section aria-label="Search results">
      <Table striped hover className="fixed" id="address-search-results">
        <TableHeaders headers={TABLE_HEADERS} sortableData={formattedData} onSort={setSortedData} />

        {dataSet && dataSet.length > 0
          ?
          <tbody>
            {pagedData.map((item) =>
              <TableRow key={item.id}
                selected={item.id === selectedDataId} onSelect={() => selectItem(item)}>
                <td>{item.buildingName}</td>
                <td className="pre-wrap">{formatAddress(item)}</td>
                <td>{getValueLabel(cities, item.municipalityId)}</td>
                <td>{getValueLabel(provinces, item.provinceId)}</td>
                <td>{getValueLabel(countries, item.countryId)}</td>
                <td>{getPostalCode(postalCodes, item.postalCodeId)}</td>
              </TableRow>
            )}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={TABLE_HEADERS.length} className="text-center">
                {dataSet?.length === 0
                  ? "Sorry, no addresses were found matching the search criteria."
                  : 'Search results will appear in this table.'
                }
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={sortedData} pageSize={pageSize} onPageChange={setPagedData} />
    </section>
  );
};

export default AddressSearchResultsTable;
